import { apiFetch } from "@/lib/apiClient";

export type EndReason =
  | "NotAvailable"
  | "LostOrBroken"
  | "Incorrect"
  | "OtherListingError";

export interface EbayListingState {
  listingType: "FixedPrice" | "Auction";
  bidCount: number;
  endTime?: string;
  startPrice?: number;
  reservePrice?: number;
  buyItNowPrice?: number;
  currentPrice?: number;
  watchCount?: number;
}

async function jsonOrError<T>(res: Response, fallback: string): Promise<T> {
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `${fallback} (${res.status})`);
  }
  return data;
}

export interface ActiveEbayListing {
  itemId: string;
  sku: string;
  title: string;
  listingType: "FixedPrice" | "Auction";
  price?: number;
  buyItNowPrice?: number;
  reservePrice?: number;
  quantity?: number;
  viewItemUrl?: string;
  startTime?: string;
  endTime?: string;
}

/** Fetches the seller's active eBay listings (GetMyeBaySelling ActiveList).
 *  Each carries SKU = the product/card id, so callers can reconcile local
 *  live-listing state against eBay. */
export async function getActiveEbayListings(): Promise<ActiveEbayListing[]> {
  const res = await apiFetch("/api/ebay/listings/active");
  return jsonOrError<ActiveEbayListing[]>(
    res,
    "Could not load active listings",
  );
}

export async function endEbayListing(
  itemId: string,
  reason: EndReason,
): Promise<void> {
  const res = await apiFetch("/api/ebay/listings/end", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ itemId, reason }),
  });
  await jsonOrError(res, "End listing failed");
}

export async function getEbayListingState(
  itemId: string,
): Promise<EbayListingState> {
  const res = await apiFetch(
    `/api/ebay/listings/${encodeURIComponent(itemId)}`,
  );
  const data = await jsonOrError<Partial<EbayListingState>>(
    res,
    "Could not load listing",
  );
  if (!data.listingType) {
    throw new Error("Listing response missing listingType");
  }
  return {
    listingType: data.listingType,
    bidCount: data.bidCount ?? 0,
    endTime: data.endTime,
    startPrice: data.startPrice,
    reservePrice: data.reservePrice,
    buyItNowPrice: data.buyItNowPrice,
    currentPrice: data.currentPrice,
    watchCount: data.watchCount,
  };
}

export interface RevisePriceBody {
  itemId: string;
  listingType: "FixedPrice" | "Auction";
  price?: number;
  startingBid?: number;
  reservePrice?: number;
}

export async function reviseEbayListingPrice(
  body: RevisePriceBody,
): Promise<void> {
  const res = await apiFetch("/api/ebay/listings/revise-price", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await jsonOrError(res, "Revise price failed");
}

export async function reviseEbayListingDetails(body: {
  itemId: string;
  title?: string;
  description?: string;
}): Promise<void> {
  const res = await apiFetch("/api/ebay/listings/revise-details", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await jsonOrError(res, "Revise details failed");
}

export interface SendOfferResult {
  sent: boolean;
  watcherCount?: number;
  newPrice?: number;
  notConnected?: boolean;
}

export async function sendEbayOfferToWatchers(body: {
  itemId: string;
  discountType: "percent" | "amount";
  discountValue: number;
  message?: string;
}): Promise<SendOfferResult> {
  const res = await apiFetch("/api/ebay/listings/send-offer", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as SendOfferResult & {
    error?: string;
  };
  if (!res.ok || !data.sent) {
    const msg = data.notConnected
      ? "Reconnect your eBay account in Settings to send offers (the new permission needs a fresh sign-in)."
      : data.error || `Send offer failed (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

export async function extendEbayListingDuration(body: {
  itemId: string;
  duration: number | "GTC";
}): Promise<{ endTime?: string }> {
  const res = await apiFetch("/api/ebay/listings/extend-duration", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return await jsonOrError<{ endTime?: string }>(res, "Extend failed");
}

export interface RelistFromListingResult {
  itemId: string;
  listingUrl?: string;
  endsAt?: string;
}

export interface ListingMessage {
  messageId: string;
  parentMessageId?: string;
  senderUserId: string;
  recipientUserId?: string;
  subject: string;
  body: string;
  receivedAt: string;
  read: boolean;
  fromBuyer: boolean;
}

export interface ListingMessagesResult {
  itemId: string;
  messages: ListingMessage[];
  unreadCount: number;
}

export async function getEbayListingMessages(
  itemId: string,
): Promise<ListingMessagesResult> {
  const res = await apiFetch(
    `/api/ebay/listings/${encodeURIComponent(itemId)}/messages`,
  );
  return await jsonOrError<ListingMessagesResult>(
    res,
    "Failed to load messages",
  );
}

export async function replyToEbayListingMessage(
  itemId: string,
  body: { parentMessageId: string; recipientUserId: string; body: string },
): Promise<void> {
  const res = await apiFetch(
    `/api/ebay/listings/${encodeURIComponent(itemId)}/messages/reply`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  await jsonOrError(res, "Failed to send reply");
}

export async function relistEbayListing(body: {
  sourceItemId: string;
  productId: string;
  title: string;
  listingType: "FixedPrice" | "Auction";
  price?: number;
  startingBid?: number;
  reservePrice?: number;
}): Promise<RelistFromListingResult> {
  const res = await apiFetch("/api/ebay/listings/relist-from-listing", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res
    .json()
    .catch(() => ({}))) as RelistFromListingResult & {
    error?: string;
  };
  if (!res.ok || !data.itemId) {
    throw new Error(data.error ?? `Relist failed (${res.status})`);
  }
  return data;
}
